/**
 * Hero2 - Pinned Scroll Integrated Hero (Refinement Pass)
 * Route: /hero2
 *
 * Architecture:
 * - Outer container: 600vh tall (scroll track)
 * - Inner sticky container: 100vh, top:0 (pinned viewport)
 * - Scroll progress [0..1] mapped to 5 stages
 * - Command Center in midground (shifted further left), headline in foreground, grid/glow in background
 * - Scroll drives stage transitions - no autoplay, no click-first
 * - Mobile: simplified vertical stage cards
 *
 * Refinement pass changes:
 * 1. Headline: increased line-height, more space before CTAs, "becomes obvious." lighter
 * 2. Command Center: shifted 5-8% further left, dark gradient mask behind headline
 * 3. Title bar label: MARKET STRUCTURE INTELLIGENCE CENTER
 * 4. Scan stage: real scanner UI (table + workflow panel + structure zone)
 * 5. Stage captions: exact copy from brief
 * 6. Vertical progress rail: larger, left-side, numbered 01-05
 * 7. Bottom tabs: thin underline, no pill/button feel
 * 8. CTA: sharper radius, less glow, more spacing
 * 9. Logo: larger, more padding from edge
 */

import React, { useEffect, useRef, useState } from "react";
import { Link } from "wouter";

// ─── Stage definitions ────────────────────────────────────────────────────────
const STAGES = [
  {
    index: 0,
    key: "price",
    tab: "PRICE",
    num: "01",
    caption: "Price is what everyone sees.",
    eyebrow: "PRICE ONLY",
    title: "Clean price action",
    description: "The workflow starts with the raw market chart, before any Structura layers are applied.",
    accent: "#94A3B8",
    glow: "rgba(148,163,184,0.10)",
    image: "/hero-layers/price.png",
    status: [
      { label: "Structure", value: "-", dim: true },
      { label: "Momentum", value: "-", dim: true },
      { label: "Trend behavior", value: "-", dim: true },
      { label: "Phase", value: "Price only", dim: false },
    ],
  },
  {
    index: 1,
    key: "structure",
    tab: "STRUCTURE",
    num: "02",
    caption: "Structure shows where the transition may begin.",
    eyebrow: "REBUILDING",
    title: "Accumulate reveals structure",
    description: "The Accumulate layer adds structure context - where price may be rebuilding before expansion.",
    accent: "#19F6A4",
    glow: "rgba(25,246,164,0.12)",
    image: "/hero-layers/structure.png",
    status: [
      { label: "Structure", value: "Active", dim: false },
      { label: "Momentum", value: "Waiting", dim: true },
      { label: "Trend behavior", value: "Pending", dim: true },
      { label: "Phase", value: "Rebuilding", dim: false },
    ],
  },
  {
    index: 2,
    key: "momentum",
    tab: "MOMENTUM",
    num: "03",
    caption: "Momentum shows whether the transition has energy.",
    eyebrow: "TRANSITION",
    title: "Horizon adds momentum context",
    description: "Horizon shows whether momentum is washed out, recovering, overheated, or weakening.",
    accent: "#33CDD3",
    glow: "rgba(51,205,211,0.13)",
    image: "/hero-layers/momentum.png",
    status: [
      { label: "Structure", value: "Active", dim: false },
      { label: "Momentum", value: "Recovering", dim: false },
      { label: "Trend behavior", value: "Pending", dim: true },
      { label: "Phase", value: "Transition", dim: false },
    ],
  },
  {
    index: 3,
    key: "trend",
    tab: "TREND",
    num: "04",
    caption: "Trend behavior confirms when character starts to change.",
    eyebrow: "LAYER 04",
    title: "Pulse confirms behavior",
    description: "Pulse markers show constructive behavior - confirming the market is shifting character, not just bouncing.",
    accent: "#818CF8",
    glow: "rgba(129,140,248,0.11)",
    image: "/hero-layers/trend.png",
    status: [
      { label: "Structure", value: "Active", dim: false },
      { label: "Momentum", value: "Recovering", dim: false },
      { label: "Trend behavior", value: "Confirming", dim: false },
      { label: "Phase", value: "Behavior shift", dim: false },
    ],
  },
  {
    index: 4,
    key: "scan",
    tab: "SCAN",
    num: "05",
    caption: "Sentinel scans where attention should go next.",
    eyebrow: "SENTINEL",
    title: "Market scanning active",
    description: "Sentinel flags markets showing structure, momentum, or behavior shifts - so you focus on what matters.",
    accent: "#44BD75",
    glow: "rgba(68,189,117,0.12)",
    image: "/sentinel-demo.png",
    status: [
      { label: "Markets flagged", value: "5", dim: false },
      { label: "Scan mode", value: "Active", dim: false },
      { label: "Next layer", value: "Pulse", dim: false },
      { label: "Phase", value: "Expansion", dim: false },
    ],
    scanRows: [
      { ticker: "TSLA",   layer: "Structure",  status: "Active",     accent: "#19F6A4", trend: "↑", momentum: "0.62" },
      { ticker: "BTCUSD", layer: "Momentum",   status: "Recovering", accent: "#33CDD3", trend: "↑", momentum: "0.37" },
      { ticker: "XLV",    layer: "Structure",  status: "Rebuilding", accent: "#44BD75", trend: "↓", momentum: "-0.39" },
      { ticker: "ORCL",   layer: "Trend",      status: "Expansion",  accent: "#818CF8", trend: "↑", momentum: "0.54" },
      { ticker: "KMB",    layer: "Structure",  status: "Bottoming",  accent: "#94A3B8", trend: "↓", momentum: "-0.81" },
    ],
    workflowRows: [
      { label: "Structure",  num: "01", pct: 100 },
      { label: "Volatility", num: "02", pct: 100 },
      { label: "Momentum",   num: "03", pct: 14  },
      { label: "Trend",      num: "04", pct: 47  },
      { label: "Money Flow", num: "05", pct: 47  },
      { label: "Expansion",  num: "06", pct: 67  },
    ],
  },
];

const TOTAL_STAGES = STAGES.length;

// ─── Scroll hook ──────────────────────────────────────────────────────────────
function useScrollStage(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const totalScroll = el.offsetHeight - window.innerHeight;
      if (totalScroll <= 0) return; // guard: container not yet laid out
      const scrolled = Math.max(0, -rect.top);
      const raw = Math.min(1, scrolled / totalScroll);

      const stageRaw = raw * TOTAL_STAGES;
      const stageIndex = Math.min(TOTAL_STAGES - 1, Math.floor(stageRaw));
      const stageProgress = Math.max(0, Math.min(1, stageRaw - stageIndex));

      setStage(stageIndex);
      setProgress(stageProgress);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [containerRef]);

  return { stage, progress };
}

// ─── Background SVG ───────────────────────────────────────────────────────────
function Background({ accent, progress }: { accent: string; progress: number }) {
  const safeProgress = isFinite(progress) ? progress : 0;
  const opacity = 0.04 + safeProgress * 0.02;
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern id="h2bg-grid" width="52" height="52" patternUnits="userSpaceOnUse">
          <path d="M 52 0 L 0 0 0 52" fill="none" stroke="#33CDD3" strokeWidth="0.4" />
        </pattern>
        <radialGradient id="h2bg-fade" cx="55%" cy="45%" r="55%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <mask id="h2bg-mask">
          <rect width="100%" height="100%" fill="url(#h2bg-fade)" />
        </mask>
        <linearGradient id="h2bg-curve" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={accent} stopOpacity="0" />
          <stop offset="40%" stopColor={accent} stopOpacity="0.3" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="url(#h2bg-grid)"
        mask="url(#h2bg-mask)"
        style={{ opacity }}
      />
      <path
        d="M -50 480 Q 200 420 380 350 T 700 280 T 1000 240 T 1400 200 T 1700 180"
        fill="none"
        stroke="url(#h2bg-curve)"
        strokeWidth="1.2"
        style={{ opacity: 0.22 + safeProgress * 0.12, transition: "opacity 0.8s" }}
      />
      <path
        d="M -50 520 Q 250 480 450 400 T 780 320 T 1100 290 T 1500 260"
        fill="none"
        stroke={accent}
        strokeWidth="0.6"
        strokeDasharray="6 10"
        style={{ opacity: 0.10 + safeProgress * 0.07, transition: "opacity 0.8s" }}
      />
    </svg>
  );
}

// ─── Scan Stage UI ────────────────────────────────────────────────────────────
function ScanStageUI({ progress, stage }: { progress: number; stage: number }) {
  const safeProgress = isFinite(progress) ? progress : 0;
  const s = STAGES[4];
  const visible = stage === 4;
  const scanRows = s.scanRows!;
  const workflowRows = s.workflowRows!

  return (
    <div
      className="absolute inset-0 z-10 flex gap-2 p-3"
      style={{
        opacity: visible ? (safeProgress > 0.1 ? 1 : 0) : 0,
        transition: "opacity 0.7s cubic-bezier(0.23,1,0.32,1)",
        background: "linear-gradient(160deg, rgba(4,12,24,0.88) 0%, rgba(2,8,16,0.92) 100%)",
        backdropFilter: "blur(2px)",
      }}
    >
      {/* Left: Scanner table */}
      <div className="flex flex-1 flex-col">
        {/* Table header */}
        <div className="mb-1.5 grid grid-cols-4 border-b border-white/[0.06] pb-1">
          {["Symbol", "Layer", "Status", "Momentum"].map((h) => (
            <span key={h} className="text-[0.42rem] font-black uppercase tracking-[0.22em] text-slate-700">{h}</span>
          ))}
        </div>
        {/* Table rows */}
        {scanRows.map((row, i) => (
          <div
            key={row.ticker}
            className="grid grid-cols-4 items-center border-b border-white/[0.04] py-1.5"
            style={{
              opacity: safeProgress > 0.08 + i * 0.08 ? 1 : 0,
              transform: safeProgress > 0.08 + i * 0.08 ? "translateX(0)" : "translateX(-6px)",
              transition: `opacity 0.45s ${i * 0.06}s, transform 0.45s ${i * 0.06}s`,
            }}
          >
            <span className="text-[0.6rem] font-black tracking-[0.1em] text-white">{row.ticker}</span>
            <span className="text-[0.55rem] font-bold text-slate-500">{row.layer}</span>
            <div className="flex items-center gap-1">
              <span
                className="h-1 w-1 rounded-full"
                style={{ backgroundColor: row.accent }}
              />
              <span className="text-[0.52rem] font-bold" style={{ color: row.accent }}>{row.status}</span>
            </div>
            <span className="text-[0.55rem] font-bold text-slate-500">
              <span className="mr-0.5" style={{ color: row.trend === "↑" ? "#19F6A4" : "#f87171" }}>{row.trend}</span>
              {row.momentum}
            </span>
          </div>
        ))}

        {/* Accumulation detected badge */}
        <div
          className="mt-auto rounded border border-[#44BD75]/20 bg-[#44BD75]/[0.06] p-2"
          style={{
            opacity: safeProgress > 0.5 ? 1 : 0,
            transition: "opacity 0.5s 0.3s",
          }}
        >
          <p className="text-[0.48rem] font-black uppercase tracking-[0.24em] text-[#44BD75]">Accumulation Detected</p>
          <p className="mt-0.5 text-[0.52rem] text-slate-500">Last signal · 27 bars ago</p>
          <div className="mt-1 h-[2px] w-full overflow-hidden rounded-full bg-white/[0.06]">
            <div className="h-full rounded-full bg-[#44BD75]" style={{ width: "62%" }} />
          </div>
        </div>
      </div>

      {/* Right: Live structure workflow panel */}
      <div
        className="flex w-[38%] flex-col gap-1.5"
        style={{
          opacity: safeProgress > 0.2 ? 1 : 0,
          transition: "opacity 0.5s 0.15s",
        }}
      >
        <p className="mb-0.5 text-[0.42rem] font-black uppercase tracking-[0.24em] text-slate-700">
          Live Structure Workflow
        </p>
        {workflowRows.map((row) => (
          <div key={row.label} className="flex items-center gap-1.5">
            <span className="w-[52px] text-[0.5rem] font-bold text-slate-400">{row.label}</span>
            <span className="text-[0.42rem] font-black text-slate-700">{row.num}</span>
            <div className="flex-1 overflow-hidden rounded-full bg-white/[0.05]" style={{ height: "3px" }}>
              <div
                className="h-full rounded-full bg-[#33CDD3]"
                style={{ width: `${row.pct}%`, opacity: row.pct > 50 ? 1 : 0.5 }}
              />
            </div>
            <span className="text-[0.42rem] font-bold text-slate-600">{row.pct}%</span>
          </div>
        ))}

        {/* Structure zone */}
        <div className="mt-auto rounded border border-[#33CDD3]/15 bg-[#33CDD3]/[0.04] p-2">
          <p className="text-[0.42rem] font-black uppercase tracking-[0.22em] text-[#33CDD3]/60">Structure Zone</p>
          <p className="mt-0.5 text-[0.52rem] font-bold text-white/70">Preparation before expansion</p>
          <p className="mt-0.5 text-[0.45rem] text-slate-600">Bullish 67% · Bearish 0%</p>
        </div>
      </div>
    </div>
  );
}

// ─── Command Center ───────────────────────────────────────────────────────────
function CommandCenter({ stage, progress }: { stage: number; progress: number }) {
  const s = STAGES[Math.max(0, Math.min(STAGES.length - 1, stage))] ?? STAGES[0];

  return (
    <div className="relative h-full w-full">
      {/* Glow halo - reduced intensity */}
      <div
        className="pointer-events-none absolute -inset-10 rounded-[3rem] blur-[60px] transition-all duration-1000"
        style={{ background: `radial-gradient(ellipse at 50% 50%, ${s.glow} 0%, transparent 70%)` }}
      />

      {/* Terminal shell */}
      <div
        className="relative flex h-full flex-col overflow-hidden border border-white/[0.07]"
        style={{
          background: "linear-gradient(160deg, #06101e 0%, #040c18 100%)",
          borderRadius: "1rem",
          boxShadow: `0 32px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.04)`,
        }}
      >
        {/* Title bar */}
        <div className="flex shrink-0 items-center justify-between border-b border-white/[0.05] bg-white/[0.02] px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[#ff6b93]/70" />
            <span className="h-2 w-2 rounded-full bg-[#f6d365]/70" />
            <span className="h-2 w-2 rounded-full bg-[#44BD75]/70" />
          </div>
          {/* MARKET STRUCTURE INTELLIGENCE CENTER label */}
          <div className="flex items-center gap-2 text-[0.48rem] font-black uppercase tracking-[0.22em] text-slate-600">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{
                backgroundColor: s.accent,
                boxShadow: `0 0 4px ${s.accent}`,
                animation: "h2pulse 2s ease-in-out infinite",
              }}
            />
            Market Structure Intelligence Center
          </div>
          <div className="w-14" />
        </div>

        {/* Chart area */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {/* Grid overlay */}
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage:
                "linear-gradient(rgba(51,205,211,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(51,205,211,0.05) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />

          {/* Base price layer - always visible */}
          <img
            src="/hero-layers/price.png"
            alt="Price chart"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
              width={988}
              height={820}
            />

          {/* Overlay layers - fade in per stage (cumulative) */}
          {STAGES.slice(1, 5).map((st) => (
            <img
              key={st.key}
              src={st.image}
              alt={st.tab}
              className="absolute inset-0 h-full w-full object-cover object-top"
              style={{
                opacity: stage >= st.index ? 1 : 0,
                transition: "opacity 0.9s cubic-bezier(0.23,1,0.32,1)",
              }}
              loading="eager"
              aria-hidden={stage < st.index}
            />
          ))}

          {/* Scan stage: now uses static chart image like other stages */}

          {/* Top gradient */}
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-14 bg-gradient-to-b from-[#06101e]/60 to-transparent" />
          {/* Bottom gradient */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-16 bg-gradient-to-t from-[#040c18]/85 to-transparent" />

          {/* Layer badge - top left */}
          <div className="absolute left-3 top-3 z-30 border border-white/[0.07] bg-[#040c18]/85 px-2.5 py-1.5 backdrop-blur-md" style={{ borderRadius: "3px" }}>
            <p className="text-[0.44rem] font-black uppercase tracking-[0.24em] text-slate-600">{s.eyebrow}</p>
            <div className="mt-0.5 flex items-center gap-1.5">
              <span
                className="h-1 w-1 rounded-full"
                style={{ backgroundColor: s.accent, boxShadow: `0 0 4px ${s.accent}` }}
              />
              <span className="text-[0.6rem] font-black uppercase tracking-[0.14em] text-white">{s.tab}</span>
            </div>
          </div>

          {/* Interpretation card - bottom left (hidden in scan mode), enlarged ~25% */}
          {stage < 4 && (
            <div
              className="absolute bottom-4 left-4 z-30 max-w-[260px] border bg-[#040c18]/92 p-3.5 backdrop-blur-md"
              style={{
                borderRadius: "3px",
                borderColor: `${s.accent}22`,
                transition: "all 0.5s cubic-bezier(0.23,1,0.32,1)",
                boxShadow: `0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.03)`,
              }}
            >
              <p
                className="text-[0.55rem] font-black uppercase tracking-[0.24em]"
                style={{ color: s.accent }}
              >
                {s.eyebrow}
              </p>
              <h3 className="mt-1 text-[0.9rem] font-black leading-[1.2] tracking-[-0.02em] text-white">
                {s.title}
              </h3>
              <p className="mt-1.5 text-[0.68rem] leading-[1.55] text-slate-400">{s.description}</p>
            </div>
          )}
        </div>

        {/* Status panel */}
        <div className="shrink-0 border-t border-white/[0.05] bg-[#030a14]/80 px-4 py-2">
          <div className="grid grid-cols-4 gap-2">
            {s.status.map((item) => (
              <div key={item.label} className="flex flex-col gap-0.5">
                <span className="text-[0.44rem] font-bold uppercase tracking-[0.18em] text-slate-700">
                  {item.label}
                </span>
                <span
                  className="text-[0.58rem] font-black"
                  style={{ color: item.dim ? "#475569" : s.accent }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow tabs - scroll progress style, not button-like */}
        <div className="shrink-0 border-t border-white/[0.05] bg-[#030a14]/60 px-3 pb-2 pt-1.5">
          <div className="flex gap-0">
            {STAGES.map((st) => {
              const isActive = st.index === stage;
              const isPast = st.index < stage;
              return (
                <div
                  key={st.key}
                  className="flex-1 px-1 pb-1.5 pt-1 text-center text-[0.46rem] font-black uppercase tracking-[0.1em] transition-all duration-500"
                  style={{
                    color: isActive ? "#fff" : isPast ? "#334155" : "#1e293b",
                    borderBottom: isActive
                      ? "1.5px solid rgba(255,255,255,0.25)"
                      : isPast
                      ? "1.5px solid rgba(255,255,255,0.06)"
                      : "1.5px solid transparent",
                    textShadow: "none",
                  }}
                >
                  {st.tab}
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Vertical stage rail ───────────────────────────────────────────────────
// Left column inside the hero - vertical list of numbered steps.
// Scroll indicator feel: no button styling, no hover movement.
function ProgressRail({ stage, accent }: { stage: number; accent: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0,
      }}
    >
      {STAGES.map((st, i) => {
        const isActive = st.index === stage;
        const isPast = st.index < stage;
        const labelColor = isActive ? accent : isPast ? "rgba(148,163,184,0.50)" : "rgba(148,163,184,0.25)";
        const numColor = isActive ? accent : isPast ? "rgba(148,163,184,0.45)" : "rgba(148,163,184,0.22)";
        const lineColor = isPast ? `${accent}55` : "rgba(255,255,255,0.08)";
        return (
          <div key={st.key} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
            {/* Left column: number + connector line */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "22px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "22px", width: "22px", position: "relative" }}>
                {isActive && (
                  <span
                    style={{
                      position: "absolute",
                      left: "-7px",
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      backgroundColor: accent,
                      boxShadow: `0 0 8px ${accent}, 0 0 14px ${accent}55`,
                      transition: "all 0.5s",
                    }}
                  />
                )}
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: numColor,
                    transition: "color 0.5s",
                    lineHeight: 1,
                  }}
                >
                  {st.num}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <div
                  style={{
                    width: "1px",
                    height: "28px",
                    backgroundColor: lineColor,
                    transition: "background-color 0.5s",
                  }}
                />
              )}
            </div>
            {/* Label */}
            <div style={{ paddingBottom: i < STAGES.length - 1 ? "28px" : "0", paddingTop: "3px" }}>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                  fontSize: "0.68rem",
                  fontWeight: isActive ? 700 : 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: labelColor,
                  transition: "color 0.5s",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {st.tab}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Mobile stage cards ───────────────────────────────────────────────────────
function MobileStageCards() {
  return (
    <div className="flex flex-col gap-4 pt-6">
      {STAGES.map((st) => (
        <div
          key={st.key}
          className="border border-white/[0.07] bg-[#06101e]/80 p-4"
          style={{ borderRadius: "3px" }}
        >
          <div className="mb-2 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: st.accent }} />
            <span className="text-[0.52rem] font-black uppercase tracking-[0.22em] text-slate-500">
              {st.eyebrow}
            </span>
          </div>
          <p className="text-sm font-black leading-snug text-white">{st.caption}</p>
          <p className="mt-1.5 text-[0.68rem] leading-relaxed text-slate-500">{st.description}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Hero2Section - embeddable hero without nav/after-scroll (for homepage use) ──
export function Hero2Section() {
  const [showPrismLoop, setShowPrismLoop] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setShowPrismLoop(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      className="relative min-h-[780px] overflow-hidden md:h-[calc(100vh-80px)] md:min-h-[600px]"
      style={{ background: "#040c18" }}
    >
      {/* ── Chart background image ── */}
      <img
        src="/hero/structura-spy-workflow.png"
        alt="Structura market structure workflow displayed on a TradingView SPY daily chart"
        className="absolute inset-0 h-full w-full object-cover object-[68%_center] md:object-center"
        style={{ opacity: 0.74 }}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width={1624}
        height={883}
      />

      {/* ── Dark gradient overlays ── */}
      {/* Left: fade chart behind headline text */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(100deg, rgba(4,12,24,0.97) 0%, rgba(4,12,24,0.88) 38%, rgba(4,12,24,0.45) 65%, rgba(4,12,24,0.10) 100%)",
        }}
      />
      {/* Bottom fade */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
        style={{
          background: "linear-gradient(to top, #040c18 0%, transparent 100%)",
        }}
      />
      {/* Top fade */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-20"
        style={{
          background: "linear-gradient(to bottom, #040c18 0%, transparent 100%)",
        }}
      />
      {/* Keep the chart readable, but let the Prism screen own the right-side focal area. */}
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-[52%] bg-[#040c18]/70 md:block" />

      {/* ── Headline content ── */}
      <div className="relative z-20 flex min-h-[780px] flex-col justify-center px-6 py-16 md:h-full md:min-h-0 md:max-w-[62%] md:px-[clamp(3rem,5vw,5rem)] md:py-0">
        {/* Badge */}
        <div
          className="mb-6 inline-flex items-center gap-2 border border-white/[0.07] bg-white/[0.03] px-3 py-1.5"
          style={{ borderRadius: "2px" }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#33CDD3]" style={{ boxShadow: "0 0 6px #33CDD3" }} />
          <span className="text-[0.56rem] font-black uppercase tracking-[0.22em] text-slate-400">
            Structura by TrendGo LLC
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-[clamp(1.25rem,5.4vw,2rem)] font-black tracking-[-0.06em] text-white md:whitespace-nowrap md:text-[clamp(2rem,3.4vw,4rem)]"
          style={{ lineHeight: "0.93" }}
        >
          Core indicators.
          <br />
          <span style={{ color: "#33CDD3" }}>One AI-powered read.</span>
        </h1>

        {/* Subline */}
        <div className="mt-8 text-[1.18rem] leading-[1.6] tracking-[-0.01em] text-slate-300">
          <p>Structura brings structure, momentum and trend together.</p>
          <p className="mt-1.5">Prism turns them into one clear reading of the chart - on TradingView and TrendSpider.</p>
          <p className="mt-3.5">No signals. No predictions.</p>
        </div>

        {/* CTAs */}
        <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
          <a
            href="/education/meet-prism"
            className="flex h-[42px] w-full items-center justify-center bg-[#21C7D7] px-6 text-[0.68rem] font-extrabold uppercase tracking-[0.1em] text-[#061018] shadow-[0_0_18px_rgba(33,199,215,0.22)] transition hover:bg-[#35DDEB] hover:shadow-[0_0_28px_rgba(33,199,215,0.32)] sm:w-auto"
            style={{ borderRadius: "3px" }}
          >
            See Prism Read a Chart →
          </a>
          <a
            href="/free-tools"
            className="w-full text-center text-sm font-bold text-slate-200 transition hover:text-white sm:w-auto sm:text-left"
          >
            Start with the free layers →
          </a>
        </div>

        {/* Platform availability */}
        <div className="mt-5 flex items-center gap-2.5">
          <span className="text-[0.54rem] font-bold uppercase tracking-[0.18em] text-slate-700">
            Available on
          </span>
          <a href="/pricing" className="opacity-35 transition hover:opacity-65">
            <img
              src="/platforms/tradingview-logo.webp"
              alt="TradingView"
              className="h-3.5 w-auto object-contain"
              loading="lazy"
              width={120}
              height={16}
            />
          </a>
          <span className="text-slate-800">·</span>
          <a
            href="https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-35 transition hover:opacity-65"
          >
            <img
              src="/platforms/trendspider-logo.webp"
              alt="TrendSpider"
              className="h-3.5 w-auto object-contain"
              loading="lazy"
              width={120}
              height={16}
            />
          </a>
        </div>

        {/* ── Prism loop: deferred until text and CTA have painted ── */}
        {showPrismLoop && (
          <div className="relative mt-7 w-full md:absolute md:left-[calc(100%+2rem)] md:top-1/2 md:mt-0 md:w-[min(52vh,32rem)] md:-translate-y-1/2">
            <div className="pointer-events-none absolute -inset-16 -z-10 rounded-[2.5rem] bg-[#040c18]/90 shadow-[0_0_80px_50px_rgba(4,12,24,0.88)] backdrop-blur-md" />
            <div className="overflow-hidden rounded-[1.5rem] border border-cyan-300/20 shadow-[0_28px_90px_rgba(51,205,211,0.18)]">
              <img
                src="/manus-storage/prism-interface-loop_6e038a1f.gif"
                alt="Prism interface showing an XAUUSD 1D request and its market read"
                className="block h-auto w-full"
                loading="lazy"
                decoding="async"
                width={600}
                height={887}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Mobile fallback ── */}
      <style>{`
        @media (max-width: 767px) {
          .hero2-desktop-content { display: none; }
        }
      `}</style>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Hero2() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { stage, progress } = useScrollStage(containerRef);
  const safeStage = Math.max(0, Math.min(STAGES.length - 1, stage));
  const s = STAGES[safeStage] ?? STAGES[0];

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => { if (window.scrollY > 60) setScrolled(true); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // "becomes obvious." opacity - lighter at early stages, slightly dimmer at later
  const obviousOpacity = stage >= 3 ? 0.35 : 0.55;

  return (
    <>
      {/* ── Scroll track: 600vh ── */}
      <div ref={containerRef} style={{ height: "600vh" }}>

        {/* ── Sticky viewport ── */}
        <div
          className="sticky top-0 h-screen overflow-hidden"
          style={{ background: "linear-gradient(160deg, #040c18 0%, #020810 100%)" }}
        >
          {/* Background layer */}
          <Background accent={s.accent} progress={progress} />

          {/* Radial glow behind Command Center */}
          <div
            className="pointer-events-none absolute inset-0 transition-all duration-1000"
            style={{
              background: `radial-gradient(ellipse 60% 55% at 70% 50%, ${s.glow} 0%, transparent 65%)`,
            }}
          />

          {/* ── Nav ── */}
          <nav className="absolute inset-x-0 top-0 z-50 flex items-center justify-between px-8 py-4 md:px-14">
            {/* Logo - official brand signet + wordmark text */}
            <Link href="/" className="flex items-center gap-3">
              <img
                src="/brand/structura-icon-512.png"
                alt=""
                className="h-11 w-11 object-contain"
                aria-hidden="true"
              width={48}
              height={48}
            />
              <div className="flex flex-col leading-none">
                <span
                  className="text-2xl font-black text-white"
                  style={{ fontFamily: "'DM Sans', 'Inter', sans-serif", letterSpacing: "-0.025em" }}
                >
                  Structura
                </span>
                <span className="mt-0.5 whitespace-nowrap text-[0.55rem] font-medium uppercase tracking-[0.1em] text-slate-500">
                  Market Structure Intelligence
                </span>
              </div>
            </Link>
            <div className="hidden items-center gap-6 text-[0.6rem] font-bold uppercase tracking-[0.18em] text-slate-500 md:flex">
              <Link href="/product" className="transition hover:text-white">Product</Link>
              <Link href="/pricing" className="transition hover:text-white">Pricing</Link>
              <Link href="/about" className="transition hover:text-white">About</Link>
              <Link href="/contact" className="transition hover:text-white">Contact</Link>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="/free-tools#signup"
                className="flex h-8 items-center bg-[#21C7D7] px-4 text-[0.58rem] font-extrabold uppercase tracking-[0.12em] text-[#061018] shadow-[0_0_14px_rgba(33,199,215,0.22)] transition hover:bg-[#35DDEB] hover:shadow-[0_0_22px_rgba(33,199,215,0.32)]"
                style={{ borderRadius: "3px" }}
              >
                Signup for Free Access
              </a>
            </div>
          </nav>

          {/* ── Desktop integrated composition ── */}
          <div className="absolute inset-0 hidden items-center md:flex" style={{ paddingTop: "56px" }}>

            {/* ── Headline - foreground, z-30 ── */}
            <div
            className="absolute left-0 top-0 z-30 flex h-full flex-col justify-center"
            style={{
              width: "54%",
              paddingTop: "56px",
              paddingLeft: "clamp(3rem, 5vw, 5rem)",
            }}
            >
              {/* Badge */}
              <div
                className="mb-6 inline-flex items-center gap-2 border border-white/[0.07] bg-white/[0.03] px-3 py-1.5"
                style={{ borderRadius: "2px" }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: s.accent, boxShadow: `0 0 6px ${s.accent}` }}
                />
                <span className="text-[0.56rem] font-black uppercase tracking-[0.22em] text-slate-400">
                  Structura by TrendGo LLC
                </span>
              </div>

              {/* Headline - large, increased line-height */}
              <h1
                className="font-black tracking-[-0.06em] text-white"
                style={{
                  fontSize: "clamp(3.2rem, 6.5vw, 7rem)",
                  lineHeight: "0.93",
                }}
              >
                Read{" "}
                <span
                  className="transition-colors duration-700"
                  style={{ color: s.accent }}
                >
                  market structure
                </span>
                <br />
                before the move
                <br />
                <span
                  className="transition-all duration-700"
                  style={{ color: `rgba(148,163,184,${obviousOpacity})` }}
                >
                  becomes obvious.
                </span>
              </h1>

              {/* Stage caption - clearly below headline, with translucent bg for readability */}
              <div
                className="mt-8 max-w-[400px] transition-all duration-500"
                key={s.key}
              >
                <div
                  className="inline-block px-0 py-0"
                  style={{}}
                >
                  <p className="text-[1.05rem] leading-[1.6] text-slate-300 tracking-[-0.01em] whitespace-nowrap">
                    {s.caption}
                  </p>
                </div>
              </div>

              {/* CTAs - more spacing, sharper radius, less glow */}
              <div className="mt-9 flex items-center gap-3">
                <a
                  href="/free-tools#signup"
                  className="flex h-[42px] items-center bg-[#21C7D7] px-6 text-[0.68rem] font-extrabold uppercase tracking-[0.1em] text-[#061018] shadow-[0_0_18px_rgba(33,199,215,0.22)] transition hover:bg-[#35DDEB] hover:shadow-[0_0_28px_rgba(33,199,215,0.32)]"
                  style={{ borderRadius: "3px" }}
                >
                  Signup for Free Access
                </a>
              </div>

              {/* Platform availability */}
              <div className="mt-5 flex items-center gap-2.5">
                <span className="text-[0.54rem] font-bold uppercase tracking-[0.18em] text-slate-700">
                  Available on
                </span>
                <a href="/pricing" className="opacity-35 transition hover:opacity-65">
                  <img
                    src="/platforms/tradingview-logo.webp"
                    alt="TradingView"
                    className="h-3.5 w-auto object-contain"
              width={120}
              height={16}
            />
                </a>
                <span className="text-slate-800">·</span>
                <a
                  href="https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-35 transition hover:opacity-65"
                >
                  <img
                    src="/platforms/trendspider-logo.webp"
                    alt="TrendSpider"
                    className="h-3.5 w-auto object-contain"
              width={120}
              height={16}
            />
                </a>
              </div>

              {/* Stage rail - directly below platform logos */}
              <div className="mt-7">
                <ProgressRail stage={stage} accent={s.accent} />
              </div>

              {/* Dark gradient mask - preserves readability where CC overlaps */}
              <div
                className="pointer-events-none absolute inset-y-0 right-0 z-[-1] w-32"
                style={{
                  background: "linear-gradient(90deg, transparent 0%, rgba(4,12,24,0.55) 100%)",
                }}
              />
            </div>

            {/* ── Command Center - midground, z-20, shifted further left ── */}
            <div
              className="absolute right-0 top-0 z-20 h-full"
              style={{
                width: "64%",        /* wider = more overlap with headline */
                paddingTop: "68px",
                paddingBottom: "28px",
                paddingRight: "20px",
              }}
            >
              <CommandCenter stage={stage} progress={progress} />
            </div>



          </div>

          {/* ── Mobile layout ── */}
          <div className="flex h-full flex-col overflow-y-auto px-5 pb-8 pt-20 md:hidden">
            <div
              className="mb-4 inline-flex items-center gap-2 self-start border border-white/[0.07] bg-white/[0.03] px-3 py-1.5"
              style={{ borderRadius: "2px" }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#33CDD3]" />
              <span className="text-[0.56rem] font-black uppercase tracking-[0.22em] text-slate-400">
                Structura by TrendGo LLC
              </span>
            </div>

            <h1 className="font-black tracking-[-0.055em] text-white" style={{ fontSize: "clamp(2rem, 10vw, 2.8rem)", lineHeight: "0.93" }}>
              Read <span className="text-[#33CDD3]">market structure</span>
              <br />
              before the move
              <br />
              <span className="text-slate-400/55">becomes obvious.</span>
            </h1>

            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Structura gives traders a structured way to read market conditions - structure, momentum, and trend behavior - in one clear workflow.
            </p>

            <div className="mt-8 flex flex-col gap-2.5 w-full">
              <a
                href="/free-tools#signup"
                className="flex h-12 w-full items-center justify-center bg-[#21C7D7] text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#061018] shadow-[0_0_18px_rgba(33,199,215,0.22)]"
                style={{ borderRadius: "3px" }}
              >
                Signup for Free Access
              </a>
            </div>

            {/* Platform availability */}
            <div className="mt-5 flex items-center gap-3">
              <span className="text-[0.6rem] font-bold uppercase tracking-[0.15em] text-slate-500">
                Available on
              </span>
              <a href="/pricing" className="opacity-50 transition hover:opacity-80">
                <img
                  src="/platforms/tradingview-logo.webp"
                  alt="TradingView"
                  className="h-5 w-auto object-contain"
                  loading="lazy"
                  width={120}
                  height={20}
                />
              </a>
              <span className="text-slate-700">·</span>
              <a
                href="https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-50 transition hover:opacity-80"
              >
                <img
                  src="/platforms/trendspider-logo.webp"
                  alt="TrendSpider"
                  className="h-5 w-auto object-contain"
                  loading="lazy"
                  width={120}
                  height={20}
                />
              </a>
            </div>

            <MobileStageCards />
          </div>

          {/* ── Scroll indicator ── */}
          <div
            className="absolute bottom-6 left-1/2 z-50 hidden -translate-x-1/2 flex-col items-center gap-1.5 transition-opacity duration-500 md:flex"
            style={{ opacity: scrolled ? 0 : 0.4 }}
            aria-hidden="true"
          >
            <span className="text-[0.46rem] font-black uppercase tracking-[0.28em] text-slate-700">Scroll</span>
            <div className="flex h-6 w-3.5 items-start justify-center rounded-full border border-slate-800 pt-1">
              <div
                className="h-1 w-0.5 rounded-full bg-slate-600"
                style={{ animation: "h2scrollDot 1.6s ease-in-out infinite" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── After-scroll section ── */}
      <div className="bg-[#020810] py-14 md:px-10">
        <div className="flex items-center justify-center gap-4 px-6 md:px-0">
          <span className="text-[0.62rem] font-black uppercase tracking-[0.32em] text-slate-500">STRUCTURE</span>
          <span className="text-[0.62rem] font-black text-[#33CDD3]/35">→</span>
          <span className="text-[0.62rem] font-black uppercase tracking-[0.32em] text-slate-500">MOMENTUM</span>
          <span className="text-[0.62rem] font-black text-[#33CDD3]/35">→</span>
          <span className="text-[0.62rem] font-black uppercase tracking-[0.32em] text-slate-500">TREND</span>
        </div>
        <p className="mt-4 text-[0.56rem] font-bold uppercase tracking-[0.2em] text-slate-700">
          Experimental Hero2 ·{" "}
          <Link href="/" className="transition hover:text-slate-500">
            ← View current homepage
          </Link>
        </p>
      </div>

      {/* ── Keyframes ── */}
      <style>{`
        @keyframes h2scrollDot {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(7px); opacity: 0.15; }
        }
        @keyframes h2pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }
      `}</style>
    </>
  );
}
